import { Fragment, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock, LockOpen, Network, X } from "lucide-react";
import { Modal } from "../ui/Modal/Modal";
import { LoadingSpinner } from "../ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../ui/ErrorFallback/ErrorFallback";
import { useRelationshipMap, type SalesDocKind } from "../../hooks/use-orders";
import { formatDate } from "../../lib/date";
import { SalesDocDetailView } from "./SalesDocDetailView";
import styles from "./RelationshipMapModal.module.css";

const DOC_LABEL_KEYS: Record<SalesDocKind, { title: string; hint: string }> = {
	delivery: { title: "delivery", hint: "viewDeliveryDetail" },
	invoice: { title: "invoice", hint: "viewInvoiceDetail" },
};

const CLOSED_ORDER_STATUSES = new Set(["closed", "cancelled"]);

interface SelectedDoc {
	kind: SalesDocKind;
	docEntry: number;
	docNum: number;
}

interface RelationshipMapModalProps {
	orderId: string | null;
	onClose: () => void;
}

export function RelationshipMapModal({ orderId, onClose }: RelationshipMapModalProps) {
	const { t } = useTranslation();
	const query = useRelationshipMap(orderId);
	const data = query.data;
	const [selectedDoc, setSelectedDoc] = useState<SelectedDoc | null>(null);

	function handleClose() {
		setSelectedDoc(null);
		onClose();
	}

	const columns: { key: string; wide?: boolean; cards: ReactNode }[] = [];
	if (data) {
		columns.push({
			key: "production",
			wide: true,
			cards:
				data.productionOrders.length === 0 ? (
					<div className={styles.card}>
						<div className={styles.cardHeader}>{t("productionOrder")}</div>
						<div className={styles.cardBody}>
							<span className={styles.empty}>—</span>
						</div>
					</div>
				) : (
					data.productionOrders.map((po) => (
						<div key={po.lineNumber} className={styles.card}>
							<div className={styles.cardHeader}>{t("productionOrder")}</div>
							<div className={[styles.cardBody, styles.productionBody].join(" ")}>
								<div className={styles.productionCode}>{po.itemCode}</div>
								<div className={styles.productionName}>{po.itemName}</div>
								<div className={styles.cardPlanned}>
									{t("plannedQty")}: {po.totalUnits}
								</div>
							</div>
						</div>
					))
				),
		});
		columns.push({
			key: "order",
			cards: (
				<MapCard
					title={t("salesOrder")}
					closed={CLOSED_ORDER_STATUSES.has(data.order.status)}
					highlighted
				>
					<div className={styles.cardNumber}>
						{data.order.documentNumber ?? data.order.sapDocNum}
					</div>
					<div className={styles.cardLine}>{formatDate(data.order.orderDate)}</div>
				</MapCard>
			),
		});
		for (const [kind, docs] of [
			["delivery", data.deliveries],
			["invoice", data.invoices],
		] as const) {
			if (docs.length === 0) continue;
			columns.push({
				key: kind,
				cards: docs.map((d) => (
					<MapCard
						key={d.docEntry}
						title={t(DOC_LABEL_KEYS[kind].title)}
						closed={d.docStatus === "C"}
						hint={t(DOC_LABEL_KEYS[kind].hint)}
						onClick={() => setSelectedDoc({ kind, docEntry: d.docEntry, docNum: d.docNum })}
					>
						<div className={styles.cardNumber}>{d.docNum}</div>
						<div className={styles.cardLine}>{formatDate(d.docDate)}</div>
						{d.numAtCard && <div className={styles.cardLine}>{d.numAtCard}</div>}
					</MapCard>
				)),
			});
		}
	}

	return (
		<Modal isOpen={!!orderId} onClose={handleClose} maxWidth={1100}>
			<div className={styles.root}>
				<div className={styles.topBar}>
					{selectedDoc ? (
						<button
							type="button"
							className={styles.topBarClose}
							onClick={() => setSelectedDoc(null)}
							aria-label={t("goBack")}
						>
							<ArrowLeft size={18} />
						</button>
					) : (
						<Network size={18} />
					)}
					<span className={styles.topBarTitle}>
						{selectedDoc ? (
							<>
								{t(DOC_LABEL_KEYS[selectedDoc.kind].title)} #{selectedDoc.docNum}
							</>
						) : (
							<>
								{t("relationshipMap")} — {t("order")} #
								{data ? (data.order.documentNumber ?? data.order.sapDocNum) : orderId}
							</>
						)}
					</span>
					<button
						type="button"
						className={styles.topBarClose}
						onClick={handleClose}
						aria-label={t("close")}
					>
						<X size={18} />
					</button>
				</div>

				<div className={styles.body}>
					{selectedDoc && orderId ? (
						<SalesDocDetailView
							orderId={orderId}
							kind={selectedDoc.kind}
							docEntry={selectedDoc.docEntry}
						/>
					) : (
						<>
							{query.isLoading && <LoadingSpinner />}
							{query.isError && <ErrorFallback onRetry={query.refetch} />}
							{data && (
								<div className={styles.canvas}>
									<div className={styles.counterpart}>
										{t("counterpart")}: <strong>{data.order.cardCode}</strong> —{" "}
										{data.order.cardName}
									</div>
									<div className={styles.flow}>
										{columns.map((col, i) => (
											<Fragment key={col.key}>
												{i > 0 && <span className={styles.arrow} />}
												<div
													className={[styles.column, col.wide ? styles.columnWide : ""].join(" ")}
												>
													{col.cards}
												</div>
											</Fragment>
										))}
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</Modal>
	);
}

interface MapCardProps {
	title: string;
	closed: boolean;
	highlighted?: boolean;
	hint?: string;
	onClick?: () => void;
	children: ReactNode;
}

function MapCard({ title, closed, highlighted, hint, onClick, children }: MapCardProps) {
	const { t } = useTranslation();
	const className = [
		styles.card,
		highlighted ? styles.cardHighlighted : "",
		onClick ? styles.cardClickable : "",
	]
		.filter(Boolean)
		.join(" ");
	const content = (
		<>
			<div className={styles.cardHeader}>{title}</div>
			<div className={styles.cardBody}>
				<span className={styles.lock} title={closed ? t("closed") : t("open")}>
					{closed ? <Lock size={22} /> : <LockOpen size={22} />}
				</span>
				{children}
			</div>
		</>
	);

	return onClick ? (
		<button type="button" className={className} onClick={onClick} title={hint}>
			{content}
		</button>
	) : (
		<div className={className}>{content}</div>
	);
}
